import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GoogleButton } from "@/components/GoogleButton";
import { LandingInstallHint } from "@/components/InstallPrompt";
import {
  ArrowRight,
  Check,
  Flame,
  Briefcase,
  FileText,
  Gift,
  ShieldCheck,
  Clock,
  Sparkles,
  Lock,
  KeyRound,
  Target,
  Send,
} from "lucide-react";
import BulletHole from "@/components/BulletHole";

const pad = (n) => String(n).padStart(2, "0");

function MiniDeck() {
  // Stylized dashboard preview — pure CSS, no live data
  const cells = Array.from({ length: 35 }, (_, i) => {
    const seed = (i * 9301 + 49297) % 233280;
    const r = seed / 233280;
    const lvl = r < 0.35 ? 0 : r < 0.6 ? 1 : r < 0.8 ? 2 : r < 0.93 ? 3 : 4;
    return lvl;
  });
  return (
    <div className="hud-frame p-5 sm:p-6 relative overflow-hidden bg-[hsl(var(--background))]">
      <BulletHole variant={3} size={60} className="absolute -top-2 -right-3 opacity-30" />
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kpi-label">Overview · May 2026</div>
          <div className="font-display text-xl sm:text-2xl font-bold uppercase mt-1">
            On<span className="text-hud">_</span>Point.
          </div>
        </div>
        <span className="hud-chip hud-chip-on whitespace-nowrap">
          <span className="status-dot" /> Live
        </span>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { l: "Habits", v: "82%" },
          { l: "Weekly", v: "67%" },
          { l: "Pipeline", v: "$24K" },
          { l: "Closed", v: "3" },
        ].map((k) => (
          <div key={k.l} className="border border-border p-2.5">
            <div className="kpi-label !text-[8px]">{k.l}</div>
            <div className="font-display font-bold text-base mt-1">{k.v}</div>
          </div>
        ))}
      </div>
      <div className="kpi-label !text-[9px] mb-2">Habit Heatmap</div>
      <div className="grid grid-cols-[repeat(35,minmax(0,1fr))] gap-[3px]">
        {cells.map((lvl, i) => (
          <div
            key={i}
            className="aspect-square border border-border/40"
            style={{
              background: [
                "hsl(var(--surface-hover))",
                "hsl(var(--oxblood) / 0.25)",
                "hsl(var(--oxblood) / 0.5)",
                "hsl(var(--oxblood) / 0.75)",
                "hsl(var(--oxblood))",
              ][lvl],
            }}
          />
        ))}
      </div>
    </div>
  );
}

const FEATURES = [
  {
    icon: Flame,
    title: "Habit Heatmap",
    body: "30-day calendar of every habit you track. The dossier knows when you slip — and when you ship.",
  },
  {
    icon: Briefcase,
    title: "Deal Pipeline",
    body: "Clients, properties, projected commission, follow-ups. Everything that makes you money in one row.",
  },
  {
    icon: FileText,
    title: "Weekly Briefing",
    body: "Sunday-ready intel: pipeline open, AR unpaid, habit cadence, follow-ups due. Print or export.",
  },
  {
    icon: Sparkles,
    title: "Build Your Own Tabs",
    body: "Tracker tabs for any service you run — consulting, agency, retainers, coaching. List tabs for anything else.",
  },
  {
    icon: Gift,
    title: "Referral Rewards",
    body: "Share your link. When two of your referrals become paying subscribers, you get a full year of your plan free — and it repeats.",
  },
  {
    icon: Lock,
    title: "Private to You",
    body: "One account, your data, your roster. No shared workspaces, no team noise.",
  },
];

function MiniStep1() {
  return (
    <div className="hud-frame p-4 bg-[hsl(var(--background))] relative">
      <div className="kpi-label flex items-center gap-2">
        <KeyRound size={10} /> Sign Up
      </div>
      <div className="mt-3 space-y-2">
        <div>
          <div className="kpi-label !text-[8px]">Email</div>
          <div className="hud-frame !border !px-2 !py-1.5 mt-1 mono text-[11px] truncate">
            you@empire.com<span className="inline-block w-0.5 h-3 bg-foreground ml-0.5 animate-blink align-middle" />
          </div>
        </div>
        <div>
          <div className="kpi-label !text-[8px]">Password</div>
          <div className="hud-frame !border !px-2 !py-1.5 mt-1 mono text-[11px]">••••••••</div>
        </div>
      </div>
      <div className="hud-chip hud-chip-on !text-[9px] mt-3 inline-flex">
        <Gift size={9} /> Referred · 50% off month one
      </div>
      <div className="hud-btn hud-btn-primary !text-[10px] !py-2 w-full justify-center mt-3 pointer-events-none">
        Start trial <ArrowRight size={10} />
      </div>
      <div className="mono text-[8px] uppercase tracking-[0.28em] text-muted-foreground text-center mt-2">
        24h · no card required
      </div>
    </div>
  );
}

function MiniStep2() {
  const habits = [
    { name: "Wake up early", days: [1, 1, 1, 0, 1, 1, 1] },
    { name: "Gym / Movement", days: [1, 1, 0, 1, 1, 0, 1] },
    { name: "Pipeline work", days: [1, 1, 1, 1, 1, 1, 1] },
    { name: "No alcohol", days: [1, 0, 1, 1, 1, 1, 0] },
  ];
  return (
    <div className="hud-frame p-4 bg-[hsl(var(--background))] relative">
      <div className="flex items-center justify-between">
        <div className="kpi-label flex items-center gap-2">
          <Target size={10} /> Daily Tracker
        </div>
        <span className="mono text-[8px] uppercase tracking-widest text-hud">today</span>
      </div>
      <div className="mt-3 space-y-1.5">
        {habits.map((h) => (
          <div key={h.name} className="flex items-center gap-2">
            <div className="flex-1 text-[10px] truncate">{h.name}</div>
            <div className="flex items-center gap-[3px]">
              {h.days.map((v, i) => (
                <div
                  key={i}
                  className="w-2.5 h-2.5 border border-border/40"
                  style={{
                    background: v
                      ? "hsl(var(--oxblood))"
                      : "hsl(var(--surface-hover))",
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="hud-divider my-3" />
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[10px]">
          <span>Hartmann · Retainer</span>
          <span className="hud-chip hud-chip-on !text-[8px]">Active</span>
        </div>
        <div className="flex items-center justify-between text-[10px]">
          <span>Coaching · Sarah</span>
          <span className="hud-chip !text-[8px]">Lead</span>
        </div>
      </div>
      <div className="mono text-[8px] uppercase tracking-[0.28em] text-muted-foreground mt-3">
        + Add custom tab
      </div>
    </div>
  );
}

function MiniStep3() {
  return (
    <div className="hud-frame p-4 bg-[hsl(var(--background))] relative">
      <div className="flex items-center justify-between">
        <div className="kpi-label flex items-center gap-2">
          <FileText size={10} /> Weekly Briefing
        </div>
        <span className="mono text-[8px] uppercase tracking-widest text-muted-foreground">Sun · May 17</span>
      </div>
      <div className="font-display font-medium text-[13px] mt-3 leading-snug">
        Execution strong. Habits locked in. <span className="text-hud">$24K</span> pipeline open.
      </div>
      <div className="hud-divider my-3" />
      <div className="space-y-1.5">
        {[
          { l: "Habits · last 7d", v: "85%" },
          { l: "Weekly tasks", v: "12/14" },
          { l: "Pipeline open", v: "$24,000" },
          { l: "Unpaid AR", v: "$2,400" },
        ].map((r) => (
          <div key={r.l} className="flex items-center justify-between text-[10px]">
            <span className="text-muted-foreground">{r.l}</span>
            <span className="num font-semibold">{r.v}</span>
          </div>
        ))}
      </div>
      <div className="hud-divider my-3" />
      <div className="flex items-center gap-1.5">
        <div className="hud-btn !text-[9px] !px-2 !py-1 pointer-events-none">PRINT</div>
        <div className="hud-btn !text-[9px] !px-2 !py-1 pointer-events-none">EMAIL</div>
        <div className="hud-btn !text-[9px] !px-2 !py-1 pointer-events-none flex items-center gap-1">
          <Send size={8} /> EXPORT
        </div>
      </div>
    </div>
  );
}

const TESTIMONIALS = [
  {
    quote: "Replaced the Google Sheet that took 3 minutes to load every morning. Same data, none of the lag. The Sunday briefing alone is worth $9.",
    name: "M. Chen",
    role: "Realtor · Pacific NW",
    stat: "11 deals tracked",
    initials: "MC",
  },
  {
    quote: "I run my whole consulting business off Command Center now. Custom tabs for retainers, content pipeline, leads. The dossier print is what I send investors quarterly.",
    name: "Ade O.",
    role: "Brand Consultant",
    stat: "$0/mo · 2 refs active",
    initials: "AO",
  },
  {
    quote: "Came for the habit tracker, stayed for the pipeline view. Two friends signed up off my code and now I'm running it free. Math checked out.",
    name: "J. Vasquez",
    role: "Solo Founder · Austin",
    stat: "47 habits/wk streak",
    initials: "JV",
  },
];

const STEPS = [
  {
    icon: KeyRound,
    title: "Sign Up",
    body: "Email + password. Trial starts immediately — 24 hours of full access, no card required.",
    Preview: MiniStep1,
  },
  {
    icon: Target,
    title: "Run Your Day",
    body: "Track habits. Move deals. Add a custom tab for whatever you actually do. Every screen saves itself.",
    Preview: MiniStep2,
  },
  {
    icon: FileText,
    title: "Get the Brief",
    body: "Every Sunday you get a one-page verdict on the week. Print it, email it, file it. Decide faster.",
    Preview: MiniStep3,
  },
];

export default function Landing() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const dateLine = now.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const timeLine = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

  return (
    <div className="min-h-screen bg-background text-foreground relative" data-testid="landing-page">
      {/* Background bullet holes */}
      <BulletHole variant={1} size={180} className="absolute top-20 right-[10%] opacity-30 pointer-events-none" />
      <BulletHole variant={2} size={120} className="absolute top-[60vh] left-[5%] opacity-25 pointer-events-none" />
      <BulletHole variant={3} size={140} className="absolute top-[120vh] right-[15%] opacity-20 pointer-events-none" />

      <LandingInstallHint />

      {/* Sticky header */}
      <header
        className="sticky top-0 z-30 border-b border-border bg-background/92 backdrop-blur-sm"
        data-testid="landing-header"
      >        <div className="max-w-7xl mx-auto flex items-center gap-2 sm:gap-4 px-3 sm:px-4 lg:px-8 h-16">
          <div className="flex items-center gap-3">
            <span className="status-dot" />
            <div>
              <div className="font-display font-bold text-lg uppercase leading-none tracking-tight">
                Command<span className="text-oud">·</span>Center
              </div>
              <div className="serif-mark text-[11px] text-muted-foreground mt-1 tracking-wide">
                For CEO's Only
              </div>
            </div>
          </div>
          <span className="flex-1" />
          <span className="hidden md:inline-flex mono uppercase text-[10px] tracking-[0.22em] text-muted-foreground">
            {dateLine} <span className="text-foreground ml-3">{timeLine}</span>
          </span>
          <Link to="/pricing" className="hidden sm:inline-flex hud-btn !text-[10px]" data-testid="landing-pricing-link">
            PRICING
          </Link>
          <Link to="/signin" className="hud-btn !text-[10px]" data-testid="landing-signin">
            SIGN IN
          </Link>
          <Link to="/signup" className="hud-btn hud-btn-primary !text-[10px]" data-testid="landing-cta-header">
            START FREE TRIAL <ArrowRight size={10} />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pt-20 lg:pt-28 pb-20 relative z-[2]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="kpi-label flex items-center gap-2 mb-5">
              <ShieldCheck size={11} /> The CEO Operating System
            </div>
            <h1
              className="display font-bold leading-[0.92] tracking-tight uppercase"
              style={{ fontSize: "clamp(2.5rem, 7vw, 5.5rem)" }}
            >
              Run your<br />
              entire empire<br />
              <span className="text-hud">_</span>from one screen.
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-muted-foreground max-w-xl leading-relaxed">
              Habits. Pipeline. Tasks. Clients. Briefings. Whatever <em>your</em> business
              tracks — Command Center is your private dossier for getting it done.
            </p>

            <div className="hud-divider my-8 max-w-md" />

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to="/signup"
                className="hud-btn hud-btn-primary text-base !px-6 !py-4"
                data-testid="landing-cta-hero"
              >
                START 1-DAY FREE TRIAL <ArrowRight size={14} />
              </Link>
              <Link
                to="/signin"
                className="hud-btn text-base !px-6 !py-4"
                data-testid="landing-signin-hero"
              >
                I have an account
              </Link>
            </div>

            <div className="max-w-xs mb-6">
              <GoogleButton next="/" label="Or sign up with Google" />
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              <span className="flex items-center gap-1.5"><Check size={11} className="text-hud" /> Free to start</span>
              <span className="flex items-center gap-1.5"><Check size={11} className="text-hud" /> No card up front</span>
              <span className="flex items-center gap-1.5"><Check size={11} className="text-hud" /> Cancel anytime</span>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-[hsl(var(--oxblood))/0.06] blur-3xl -z-10" />
            <MiniDeck />
            <div className="mono text-[9px] uppercase tracking-[0.28em] text-muted-foreground text-center mt-3">
              Live preview · simulated data
            </div>
          </div>
        </div>
      </section>

      {/* Stat strip */}
      <section className="border-y border-border bg-[hsl(var(--surface))/0.4] py-10 relative z-[2]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
          {[
            { v: "$0", l: "To start" },
            { v: "24h", l: "Free trial" },
            { v: "9", l: "Built-in sectors" },
            { v: "∞", l: "Custom tabs" },
          ].map((s) => (
            <div key={s.l} className="text-center">
              <div className="display font-bold text-3xl sm:text-4xl text-hud">{s.v}</div>
              <div className="mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground mt-2">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28 relative z-[2]" data-testid="how-it-works">
        <div className="max-w-3xl mb-12 lg:mb-16">
          <div className="kpi-label">Section 01 · How it works</div>
          <h2 className="display text-3xl sm:text-5xl font-bold uppercase tracking-tight mt-4 leading-[0.95]">
            From sign-up to <span className="text-hud">_</span>weekly verdict.<br />
            Three moves.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground mt-5 leading-relaxed">
            No onboarding marathon, no team setup, no per-seat math. Sign in, track your day,
            read the Sunday brief. That's the loop.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            const Preview = step.Preview;
            return (
              <div
                key={step.title}
                className="relative animate-slideup"
                style={{ animationDelay: `${i * 140}ms` }}
                data-testid={`step-${i + 1}`}
              >
                {/* Connector arrow between cards on desktop */}
                {i < STEPS.length - 1 && (
                  <div className="hidden md:flex absolute -right-3 top-12 z-10 items-center justify-center w-6 h-6 bg-background border border-[hsl(var(--oxblood))]">
                    <ArrowRight size={10} className="text-[hsl(var(--oxblood))]" />
                  </div>
                )}
                <div className="hud-frame p-6 h-full flex flex-col group transition-transform duration-300 hover:-translate-y-1">
                  <div className="flex items-start justify-between mb-5">
                    <div
                      className="display font-bold text-5xl text-[hsl(var(--oxblood))/0.85] leading-none"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      0{i + 1}
                    </div>
                    <div className="w-9 h-9 border border-border flex items-center justify-center bg-[hsl(var(--surface))]">
                      <Icon size={14} className="text-foreground" />
                    </div>
                  </div>
                  <div className="display font-bold text-xl sm:text-2xl uppercase tracking-tight">
                    {step.title}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-3 mb-6">
                    {step.body}
                  </p>

                  {/* The mini preview */}
                  <div className="mt-auto">
                    <Preview />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 flex justify-center">
          <Link
            to="/signup"
            className="hud-btn hud-btn-primary text-base !px-7 !py-4"
            data-testid="how-it-works-cta"
          >
            TRY THE FULL LOOP · FREE FOR 24H <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Field Reports (Testimonials) */}
      <section className="border-y border-border bg-[hsl(var(--surface))/0.5] relative z-[2]" data-testid="field-reports">
        <div className="tread-plate h-1.5" />
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12 lg:mb-16">
            <div className="max-w-2xl">
              <div className="kpi-label">Section 02 · Field Reports</div>
              <h2 className="display text-3xl sm:text-5xl font-bold uppercase tracking-tight mt-4 leading-[0.95]">
                What operators are <span className="text-hud">_</span>saying.
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground mt-5 leading-relaxed">
                Unedited intel from the field. Names redacted to initials by request.
              </p>
            </div>
            <div className="hud-chip hud-chip-on">
              <span className="status-dot" /> Confidential · Verified
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={t.name}
                className="hud-frame p-6 relative group transition-transform duration-300 hover:-translate-y-1 animate-slideup flex flex-col"
                style={{ animationDelay: `${i * 120}ms` }}
                data-testid={`testimonial-${i + 1}`}
              >
                {/* corner brand tag */}
                <div className="absolute top-0 right-0 mono text-[8px] uppercase tracking-[0.28em] text-muted-foreground bg-background border-l border-b border-border px-2 py-1">
                  FILE · {String(i + 1).padStart(3, "0")}
                </div>

                {/* Big quote mark */}
                <div
                  className="font-display font-bold leading-none text-[hsl(var(--oxblood))/0.4] mb-2"
                  style={{ fontSize: "3rem" }}
                  aria-hidden
                >
                  “
                </div>

                <blockquote className="text-sm sm:text-base leading-relaxed font-display font-medium flex-1">
                  {t.quote}
                </blockquote>

                <div className="hud-divider my-5" />

                <footer className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 border border-[hsl(var(--oxblood))] flex items-center justify-center bg-[hsl(var(--background))] mono text-xs font-bold text-hud flex-shrink-0"
                    aria-hidden
                  >
                    {t.initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold uppercase text-sm tracking-tight truncate">
                      {t.name}
                    </div>
                    <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground truncate">
                      {t.role}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="mono text-[8px] uppercase tracking-[0.22em] text-muted-foreground">Stat</div>
                    <div className="mono text-[10px] text-hud whitespace-nowrap mt-0.5">{t.stat}</div>
                  </div>
                </footer>
              </article>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-6 mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
            <span className="flex items-center gap-2">
              <Check size={11} className="text-hud" /> Solo-operator focus
            </span>
            <span className="flex items-center gap-2">
              <Check size={11} className="text-hud" /> No team-seat fees
            </span>
            <span className="flex items-center gap-2">
              <Check size={11} className="text-hud" /> Private to you
            </span>
          </div>
        </div>
        <div className="tread-plate h-1.5" />
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28 relative z-[2]">
        <div className="max-w-3xl mb-12 lg:mb-16">
          <div className="kpi-label">Section 03 · Capabilities</div>
          <h2 className="display text-3xl sm:text-5xl font-bold uppercase tracking-tight mt-4 leading-[0.95]">
            Built for operators who'd rather <span className="text-hud">ship</span> than schedule.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground mt-5 leading-relaxed">
            Every screen has one job: surface what matters, hide what doesn't,
            print when you need a paper trail. Nothing else.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="hud-frame p-6 relative group" data-testid={`feature-${i}`}>
                <div className="flex items-center gap-2 mb-4">
                  <Icon size={14} className="text-hud" />
                  <div className="mono text-[9px] uppercase tracking-[0.32em] text-muted-foreground">
                    Item · {pad(i + 1)}
                  </div>
                </div>
                <div className="display font-bold text-xl uppercase tracking-tight mb-3">
                  {f.title}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing + referral hook */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28 relative z-[2]">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          <div className="lg:col-span-3 hud-frame p-8 lg:p-10 relative">
            <BulletHole variant={2} size={80} className="absolute -top-2 right-4 opacity-50" />
            <div className="kpi-label flex items-center gap-2"><Clock size={11} /> The Plans</div>
            <div className="flex items-baseline gap-3 mt-4">
              <div className="display font-bold leading-none" style={{ fontSize: "clamp(3rem, 8vw, 5rem)" }}>
                $0
              </div>
              <div className="mono text-sm uppercase tracking-[0.2em] text-muted-foreground">TO START</div>
            </div>
            <div className="mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground mt-2">
              Free forever plan · Pro $10/mo · Team $20/mo · cancel anytime
            </div>

            <div className="hud-divider my-7" />

            <ul className="space-y-3">
              {[
                "Free: habit tracker, weekly tasks, one pipeline, dashboard",
                "Pro: unlimited everything + drag-and-drop, priority & time scheduling",
                "Pro: AI schedule generation, daily brief, Google Calendar sync",
                "Team: shared workspaces, task assignment, roles & team analytics",
                "Weekly briefing — readable, printable, exportable",
                "Referrals: 2 paying sign-ups = 1 full year of your plan, free",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <Check size={14} className="text-hud mt-0.5 flex-shrink-0" />
                  <span className="text-sm sm:text-base">{line}</span>
                </li>
              ))}
            </ul>

            <Link
              to="/signup"
              className="hud-btn hud-btn-primary mt-8 text-base !px-6 !py-4"
              data-testid="landing-cta-pricing"
            >
              START YOUR FREE TRIAL <ArrowRight size={14} />
            </Link>
          </div>

          <div className="lg:col-span-2 hud-frame p-8 lg:p-10 bg-[hsl(var(--surface))]">
            <div className="kpi-label flex items-center gap-2"><Gift size={11} /> Referral · The Loop</div>
            <h3 className="display font-bold text-2xl uppercase tracking-tight mt-4 leading-tight">
              Refer two.<br />
              Get a year<br />
              <span className="text-hud">free.</span>
            </h3>

            <div className="hud-divider my-6" />

            <div className="space-y-5">
              <div>
                <div className="kpi-label">They get</div>
                <div className="font-display text-lg font-medium mt-1">50% off their first month</div>
              </div>
              <div>
                <div className="kpi-label">You get</div>
                <div className="font-display text-lg font-medium mt-1">2 paying referrals = 1 year free — repeatable</div>
              </div>
              <div>
                <div className="kpi-label">Math</div>
                <div className="font-display text-lg font-medium mt-1">
                  2 active referrals = <span className="text-hud">free</span>
                </div>
              </div>
            </div>

            <div className="hud-divider my-6" />

            <p className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Sign up, get your code instantly. Share. Stack discounts. No cap on signups — only on your bill (it stops at free).
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border relative z-[2]">
        <div className="tread-plate h-1.5" />
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28 text-center relative">
          <BulletHole variant={1} size={200} className="absolute top-4 left-1/2 -translate-x-1/2 opacity-20 pointer-events-none" />
          <div className="kpi-label">End · Begin</div>
          <h2 className="display font-bold uppercase tracking-tight mt-5 leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 6vw, 4.5rem)" }}>
            Stop juggling tabs.<br />
            <span className="text-hud">_</span>Start running point.
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto mt-6">
            One screen. Your operations. Free to try for 24 hours — no card, no friction.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/signup"
              className="hud-btn hud-btn-primary text-base !px-7 !py-4"
              data-testid="landing-cta-final"
            >
              START FREE TRIAL <ArrowRight size={14} />
            </Link>
            <Link to="/signin" className="hud-btn text-base !px-7 !py-4">
              SIGN IN
            </Link>
          </div>
          <div className="mt-5 max-w-xs mx-auto">
            <GoogleButton next="/" label="Or continue with Google" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8 pb-24 md:pb-8 relative z-[2]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="font-display font-bold text-base uppercase tracking-tight">
              Command<span className="text-hud">·</span>Center
            </div>
            <div className="mono text-[9px] uppercase tracking-[0.28em] text-muted-foreground mt-1">
              For CEO's Only
            </div>
          </div>
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-4">
            <Link to="/pricing" className="hover:text-foreground transition-colors" data-testid="footer-pricing-link">
              PRICING
            </Link>
            <span>© {new Date().getFullYear()} Command Center · Built for operators</span>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA bar */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-md animate-slideup"
        data-testid="mobile-sticky-cta"
      >
        <div className="tread-plate h-1" />
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base leading-none">
              $0<span className="mono text-[10px] text-muted-foreground ml-1 tracking-widest">TO START</span>
            </div>
            <div className="mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground mt-1">
              Free plan · no card
            </div>
          </div>
          <Link
            to="/signup"
            className="hud-btn hud-btn-primary !text-[11px] !px-4 !py-3 whitespace-nowrap"
            data-testid="mobile-sticky-cta-button"
          >
            START TRIAL <ArrowRight size={11} />
          </Link>
        </div>
      </div>
    </div>
  );
}

